import { environment } from 'src/environments/environment';

export interface MenuItem {
  id?: string;
  name: string;
  /** Con `type: 'svg'`, `name` es una clave de MENU_SVG_ICONS (iconos de trazo del prediseño). */
  icon?: { type: 'keenicon' | 'fas' | 'svg'; name: string };
  permission_page?: string;
  routerLink?: string[] | string;
  section_title?: boolean;
  badge?: string;
  /** Número junto al título (p. ej. solicitudes pendientes); no se muestra si es 0. */
  count?: number;
  /** Tooltip del número (clave i18n, recibe `count`). */
  countTitle?: string;
  exact?: boolean;
  /**
   * Otras rutas que pertenecen a este item aunque no cuelguen de su routerLink
   * (p. ej. el detalle de un viaje, /travel/:id, es parte de «Viajes»): con
   * ellas abiertas, el item se marca activo en el menú.
   */
  activeRoutes?: string[];
  disabled?: boolean;
  /** Bloqueado (onboarding o plan sin esta función): se muestra con candado y sin navegación. */
  locked?: boolean;
  /** Texto del tooltip del candado; si no se indica, se usa el de onboarding por defecto. */
  lockedReason?: string;
  /** El candado viene del plan activo (no del onboarding): al hacer click se ofrece subir a Plan de Incentivos. */
  lockedByPlan?: boolean;
  queryParams?: Record<string, string>;
  submenus?: MenuItem[];
}

const DRIVERS_PRO_MENU: MenuItem[] = [
  {
    id: 'drivers_pro_section_title',
    name: 'SIDEBAR.DRIVERS_PRO',
    section_title: true,
  },
  {
    id: 'admin_document_reviews',
    name: 'SIDEBAR.DOCUMENT_REVIEWS',
    permission_page: 'admin_document_reviews',
    icon: { type: 'svg', name: 'clipboard-check' },
    routerLink: ['/admin', 'document-reviews'],
  },
  {
    id: 'admin_fleets',
    name: 'SIDEBAR.FLEETS',
    permission_page: 'admin_fleets',
    icon: { type: 'svg', name: 'car' },
    routerLink: ['/admin', 'fleets'],
  },
  {
    id: 'admin_professional_drivers',
    name: 'SIDEBAR.PROFESSIONAL_DRIVERS',
    permission_page: 'admin_professional_drivers',
    icon: { type: 'svg', name: 'id-card' },
    routerLink: ['/admin', 'professional-drivers'],
  },
  {
    id: 'admin_passenger_fees',
    name: 'SIDEBAR.PASSENGER_FEES',
    permission_page: 'admin_passenger_fees',
    icon: { type: 'svg', name: 'badge-percent' },
    routerLink: ['/admin', 'passenger-fees'],
  },
  {
    id: 'admin_driver_challenges',
    name: 'SIDEBAR.DRIVER_CHALLENGES',
    permission_page: 'admin_driver_challenges',
    icon: { type: 'svg', name: 'target' },
    routerLink: ['/admin', 'driver-challenges'],
  },
];

export const MENU_CONFIG: MenuItem[] = [
  {
    id: 'dashboard',
    name: 'MENU.DASHBOARD',
    icon: { type: 'svg', name: 'layout-grid' },
    routerLink: ['/dashboard'],
  },
  // Gestión de actividad: lo que se supervisa a diario. "Viajes" abre el menú
  // por ser la entrada más usada del backoffice.
  {
    id: 'activity_section_title',
    name: 'SIDEBAR.ACTIVITY_MANAGEMENT',
    section_title: true,
  },
  {
    id: 'admin_home',
    name: 'SIDEBAR.ADMIN_HOME',
    permission_page: 'admin_users',
    icon: { type: 'svg', name: 'house' },
    routerLink: ['/admin', 'home'],
  },
  {
    id: 'admin_travel_list',
    name: 'SIDEBAR.TRAVEL_LIST',
    permission_page: 'admin_users',
    icon: { type: 'svg', name: 'map' },
    routerLink: ['/admin', 'travel-list'],
    activeRoutes: ['/travel'],
  },
  // Rides VTC (Uber, Bolt, Cabify). Mismo patrón que rides_config: el
  // permiso 'admin_rides' no está asignado a ningún rol todavía, así que solo
  // lo ve el superadmin; el backend exige el operation_id de cada ruta.
  {
    id: 'admin_rides',
    name: 'SIDEBAR.RIDES',
    permission_page: 'admin_rides',
    icon: { type: 'svg', name: 'car' },
    routerLink: ['/admin', 'rides'],
  },
  // Mesa de reservas de grupo: el backend solo admite al superadmin (sin
  // operationId que se pueda dar a otro rol), así que el permiso no se asigna
  // y la ruta lleva SuperAdminGuard.
  {
    id: 'admin_ride_bookings',
    name: 'SIDEBAR.RIDE_BOOKING_DESK',
    permission_page: 'admin_ride_bookings',
    icon: { type: 'svg', name: 'calendar-clock' },
    routerLink: ['/admin', 'ride-bookings'],
  },
  // Catálogo de eventos (conciertos, partidos, festivales…) y sus categorías.
  // Permisos propios que no tiene ningún rol todavía: hoy lo ve solo el
  // superadmin, igual que el backend (trips-core), que responde 403 al resto.
  {
    id: 'admin_events_section',
    name: 'SIDEBAR.EVENTS_SECTION',
    icon: { type: 'svg', name: 'ticket' },
    submenus: [
      {
        id: 'admin_events',
        name: 'SIDEBAR.EVENTS',
        permission_page: 'admin_events',
        routerLink: ['/admin', 'events'],
      },
      {
        id: 'admin_event_categories',
        name: 'SIDEBAR.EVENT_CATEGORIES',
        permission_page: 'admin_event_categories',
        routerLink: ['/admin', 'event-categories'],
      },
    ],
  },
  {
    id: 'admin_users',
    name: 'SIDEBAR.WAIIS_USERS',
    permission_page: 'admin_users',
    icon: { type: 'svg', name: 'users' },
    routerLink: ['admin', 'users'],
  },
  {
    id: 'admin_fraud',
    name: 'SIDEBAR.FRAUD',
    permission_page: 'admin_users',
    icon: { type: 'svg', name: 'shield-alert' },
    routerLink: ['/admin', 'fraud'],
  },
  // Agrupadas bajo "Soporte" (antes 4 entradas sueltas en el sidebar) para
  // reflejar la jerarquía del prediseño: Chat en tiempo real + Historial de
  // agentes + Reclamaciones + su taxonomía viven todas bajo el mismo epígrafe.
  // Sin `permission_page` propio: el grupo se muestra en cuanto cualquiera de
  // sus hijos sea visible (ver `PermissionsService.getVisibleMenuItems`), sin
  // necesidad de un permiso dedicado solo para el contenedor.
  {
    id: 'admin_payout_reviews',
    name: 'SIDEBAR.PAYOUT_REVIEWS',
    permission_page: 'payout_reviews',
    icon: { type: 'svg', name: 'banknote-arrow-up' },
    routerLink: ['/admin', 'payout-reviews'],
  },
  {
    id: 'admin_support_section',
    name: 'SIDEBAR.SUPPORT',
    icon: { type: 'svg', name: 'headphones' },
    submenus: [
      {
        id: 'admin_support_chat',
        name: 'WAIIS.SUPPORT_CHAT.TITLE',
        permission_page: 'admin_support_chat',
        routerLink: ['/admin', 'support-chat'],
      },
      // Historial de conversaciones atendidas por agentes automáticos (in-app,
      // WhatsApp, llamadas). Su permiso es propio y aún no lo tiene ningún rol,
      // así que hoy la ve solo el superadmin (menú completo sin filtrar); el
      // backend debe exigir el mismo permiso cuando exponga el endpoint (ver
      // pages/waiis/agent-conversations/backend-contract.md).
      {
        id: 'admin_agent_conversations',
        name: 'SIDEBAR.AGENT_CONVERSATIONS',
        permission_page: 'admin_agent_conversations',
        routerLink: ['/admin', 'agent-conversations'],
      },
      {
        id: 'admin_reclamations',
        name: 'WAIIS.RECLAMATIONS.TITLE',
        permission_page: 'admin_reclamations',
        routerLink: ['/admin', 'reclamations'],
      },
      {
        id: 'admin_reclamation_taxonomy',
        name: 'WAIIS.RECLAMATION_TAXONOMY.TITLE',
        permission_page: 'admin_reclamation_taxonomy',
        routerLink: ['/admin', 'reclamation-taxonomy'],
      },
    ],
  },
  // Taxi y VTC (App Drivers, DDEV-5 / DDEV-6): verificación de documentos,
  // flotas y conductores profesionales. Todavía sin backend: solo se ve con el
  // mock de flotas (environment.fleetMock, local). Cuando existan los
  // endpoints, quitar la condición (docs/flotas-drivers/README.md).
  ...(environment.fleetMock ? DRIVERS_PRO_MENU : []),
  // Dinero (WS-2154 / WS-2155): el dashboard económico sale de
  // "Administración" a su propia sección, como en el prediseño del módulo de
  // Economía (Modulo-Economia.html).
  {
    id: 'money_section_title',
    name: 'SIDEBAR.MONEY',
    permission_page: 'economyc_dashboard',
    section_title: true,
  },
  // Página única, como en el prediseño (sustituye a Reportes, Movimientos,
  // Usuarios generales y Usuarios corporación, que se eliminaron).
  {
    id: 'economyc_dashboard_section',
    name: 'SIDEBAR.ECONOMIC_DASHBOARD',
    permission_page: 'economyc_dashboard',
    icon: { type: 'svg', name: 'line-chart' },
    routerLink: ['/economic'],
  },
  {
    id: 'corporations_section_title',
    name: 'SIDEBAR.CORPORATIONS',
    permission_page: 'corporations',
    section_title: true,
  },
  {
    id: 'corporations',
    name: 'SIDEBAR.CORPORATIONS',
    permission_page: 'corporations',
    icon: { type: 'svg', name: 'building' },
    routerLink: ['/admin/companies'],
  },
  // Solicitudes de grandes cuentas de todos los partners (reasignar, crear la
  // empresa). Permiso propio que no tiene ningún rol: solo la ve el superadmin,
  // que ve el menú entero sin filtrar; el backend también la limita a él.
  {
    id: 'corporations_partner_requests',
    name: 'SIDEBAR.PARTNER_REQUESTS',
    permission_page: 'partner_requests',
    icon: { type: 'svg', name: 'inbox' },
    routerLink: ['/admin/partner-requests'],
  },
  // Alta y configuración de los distribuidores que traen sus propias empresas.
  // Mismo permiso que el listado: hoy es pantalla de admin de WAIIS.
  {
    id: 'corporations_partners',
    name: 'SIDEBAR.PARTNERS',
    permission_page: 'corporations',
    icon: { type: 'fas', name: 'fa-handshake' },
    routerLink: ['/admin/partners'],
  },

  // Sedes y Turnos ya se consultan desde la ficha de cada empresa
  // (/admin/companies/:id); las rutas admin/company/offices y
  // admin/company/shifts siguen existiendo, solo dejan de aparecer en el menú.
  {
    id: 'marketing_section_title',
    name: 'SIDEBAR.MARKETING',
    section_title: true,
  },
  {
    id: 'marketing_users',
    name: 'SIDEBAR.MARKETING_USERS',
    permission_page: 'marketing_users',
    icon: { type: 'svg', name: 'user-round' },
    routerLink: ['marketing', 'users'],
  },
  // Programa de referidos. Su permiso es propio y no lo tiene ningún rol
  // todavía: hoy la página es solo para superadmin, que ve el menú entero sin
  // filtrar, y en el backend la ReferralPolicy exige lo mismo.
  {
    id: 'marketing_referrals',
    name: 'SIDEBAR.REFERRALS',
    permission_page: 'marketing_referrals',
    icon: { type: 'svg', name: 'share-2' },
    routerLink: ['marketing', 'referrals'],
  },
  // Promociones del backoffice en una sola página con pestañas: la
  // «Bonificación precio completo» (PVP, con consecuencia de fraude) y la promo
  // por viajes (WS-2414). Mismo patrón de permisos que marketing_referrals:
  // 'promos' no está asignado a ningún rol todavía, así que solo la ve el
  // superadmin hasta que se dé a los comerciales.
  {
    id: 'marketing_promos',
    name: 'SIDEBAR.PROMOS',
    permission_page: 'promos',
    icon: { type: 'svg', name: 'gift' },
    routerLink: ['marketing', 'promos'],
  },
  // Correos que envía WAIIS (plantillas, cabecera y pie), editables en comms.
  // La página 'email_templates' llega en `/me` a admin y marketing.
  {
    id: 'marketing_emails',
    name: 'SIDEBAR.EMAILS',
    permission_page: 'email_templates',
    icon: { type: 'svg', name: 'mail' },
    routerLink: ['marketing', 'emails'],
  },
  // Push manual a usuarios concretos. 'communication_push' no está asignada a
  // ningún rol, así que solo la ve el superadmin; la ruta lleva además
  // SuperAdminGuard porque POST notifications/send no comprueba permisos.
  {
    id: 'marketing_push',
    name: 'SIDEBAR.PUSH_NOTIFICATIONS',
    permission_page: 'communication_push',
    icon: { type: 'svg', name: 'bell' },
    routerLink: ['marketing', 'push'],
  },
  // Anuncios por zona en Meta (trips-core). Cada pantalla tiene su página
  // (zone_ad_imbalance, zone_ad_drafts, zone_ad_templates, zone_ad_settings),
  // sin rol asignado todavía: solo las ve el superadmin. El padre no lleva
  // página para que se vea con cualquiera de ellas. El backend exige además
  // los operationId de cada endpoint (zoneAdImbalanceIndex, zoneAdDrafts*,
  // zoneAdTemplates*, zoneAdSettings*).
  {
    id: 'marketing_zone_ads',
    name: 'SIDEBAR.ZONE_ADS',
    icon: { type: 'svg', name: 'megaphone' },
    submenus: [
      {
        name: 'SIDEBAR.ZONE_ADS_IMBALANCE',
        permission_page: 'zone_ad_imbalance',
        routerLink: ['marketing', 'zone-ads', 'imbalance'],
      },
      {
        name: 'SIDEBAR.ZONE_ADS_DRAFTS',
        permission_page: 'zone_ad_drafts',
        routerLink: ['marketing', 'zone-ads', 'drafts'],
      },
      {
        name: 'SIDEBAR.ZONE_ADS_TEMPLATES',
        permission_page: 'zone_ad_templates',
        routerLink: ['marketing', 'zone-ads', 'templates'],
      },
      {
        name: 'SIDEBAR.ZONE_ADS_SETTINGS',
        permission_page: 'zone_ad_settings',
        routerLink: ['marketing', 'zone-ads', 'settings'],
      },
    ],
  },
  {
    id: 'administration_section_title',
    name: 'SIDEBAR.ADMINISTRATION',
    permission_page: 'admin_users',
    section_title: true,
  },
  {
    id: 'authorization_dashboard',
    name: 'SIDEBAR.AUTHORIZATION_DASHBOARD',
    permission_page: 'authorization_dashboard',
    icon: { type: 'svg', name: 'lock' },
    submenus: [
      {
        name: 'SIDEBAR.ROLES',
        permission_page: 'authorization_roles',
        routerLink: ['/authorization', 'roles'],
      },
      {
        name: 'SIDEBAR.USER_ROLE_CORPORATIONS',
        permission_page: 'authorization_user_role_corporations',
        routerLink: ['/authorization', 'user-role-corporations'],
      },
    ],
  },
  // {
  //   id: 'admin_incentives',
  //   name: 'SIDEBAR.INCENTIVES',
  //   permission_page: 'admin_incentives',
  //   icon: { type: 'keenicon', name: 'star' },
  //   routerLink: ['admin', 'company', 'incentives'],
  // },
  // {
  //   id: 'admin_complaints',
  //   name: 'SIDEBAR.COMPLAINTS',
  //   permission_page: 'admin_complaints',
  //   icon: { type: 'keenicon', name: 'notification-status' },
  //   routerLink: ['admin', 'complaints'],
  // },

  {
    id: 'corporation_users',
    name: 'SIDEBAR.CORPORATION_USERS',
    permission_page: 'corporation_users',
    icon: { type: 'svg', name: 'contact' },
    routerLink: ['/admin/all-corporation-users'],
  },
  // {
  //   id: 'reports_section_title',
  //   permission_page: 'reports_waiis_funnel',
  //   name: 'SIDEBAR.REPORTS_SECTION',
  //   section_title: true,
  // },

  // {
  //   id: 'funnel_report_waiis',
  //   name: 'SIDEBAR.FUNNEL',
  //   permission_page: 'reports_waiis_funnel',
  //   icon: { type: 'keenicon', name: 'chart' },
  //   routerLink: ['/reports', 'waiis', 'funnel'],
  // },
  // {
  //   id: 'travel_report',
  //   name: 'SIDEBAR.TRAVELS',
  //   permission_page: 'reports_travel',
  //   icon: { type: 'keenicon', name: 'airplane' },
  //   routerLink: ['/reports', 'travel'],
  // },
  // {
  //   id: 'conversion_report',
  //   name: 'SIDEBAR.CONVERSION',
  //   permission_page: 'reports_conversion',
  //   icon: { type: 'keenicon', name: 'dollar' },
  //   routerLink: ['reports', 'conversion'],
  // },
  // {
  //   id: 'usage_report',
  //   name: 'SIDEBAR.USAGE',
  //   permission_page: 'reports_usage',
  //   icon: { type: 'keenicon', name: 'delivery' },
  //   routerLink: ['/reports', 'usage'],
  // },

  {
    id: 'credits_section_title',
    name: 'SIDEBAR.CREDITS',
    permission_page: 'credits_providers',
    section_title: true,
  },
  {
    id: 'credit_providers',
    name: 'SIDEBAR.PROVIDERS',
    permission_page: 'credits_providers',
    icon: { type: 'svg', name: 'briefcase' },
    routerLink: ['/coupons', 'providers'],
  },
  {
    id: 'credit_gift_cards',
    name: 'SIDEBAR.GIFT_CARDS',
    permission_page: 'credits_gift_cards',
    icon: { type: 'svg', name: 'gift' },
    routerLink: ['/coupons', 'coupons'],
  },
  {
    id: 'credit_codes',
    name: 'SIDEBAR.CODES',
    permission_page: 'credits_codes',
    icon: { type: 'svg', name: 'ticket' },
    routerLink: ['/coupons', 'codes'],
  },
  {
    id: 'credit_promotional',
    name: 'SIDEBAR.PROMOTIONAL_CODES',
    permission_page: 'credits_promotional_codes',
    icon: { type: 'svg', name: 'badge-percent' },
    routerLink: ['/coupons', 'promotional-codes'],
  },
  {
    id: 'parking_section_title',
    name: 'SIDEBAR.PARKING_SECTION',
    permission_page: 'parking_section',
    section_title: true,
  },
  {
    id: 'parking_spaces',
    name: 'SIDEBAR.PARKING_SPACES',
    permission_page: 'parking_spaces',
    icon: { type: 'svg', name: 'square-parking' },
    routerLink: ['/parkings', 'spaces'],
  },
  {
    id: 'parking_reservations',
    name: 'SIDEBAR.PARKING_RESERVATIONS',
    permission_page: 'parking_reservations',
    icon: { type: 'svg', name: 'calendar-check' },
    routerLink: ['/parkings', 'reservations'],
  },
  {
    id: 'parking_credits',
    name: 'SIDEBAR.PARKING_CREDITS',
    permission_page: 'parking_credits',
    icon: { type: 'svg', name: 'coins' },
    routerLink: ['/parkings', 'credits'],
  },
  {
    id: 'pooling_runners_section_title',
    name: 'SIDEBAR.POOLING_RUNNERS',
    permission_page: 'pooling_runners',
    section_title: true,
  },
  {
    id: 'pooling_runners',
    name: 'SIDEBAR.POOLING_RUNNERS',
    permission_page: 'pooling_runners',
    icon: { type: 'svg', name: 'activity' },
    submenus: [
      {
        name: 'SIDEBAR.POOLING_RUNNERS',
        permission_page: 'pooling_runners',
        routerLink: ['/pooling-runners'],
      },
    ],
  },
  // {
  //   id: 'communication_section_title',
  //   name: 'SIDEBAR.COMMUNICATION',
  //   section_title: true,
  // },
  // {
  //   id: 'push_notifications',
  //   name: 'SIDEBAR.PUSH',
  //   permission_page: 'communication_push',
  //   icon: { type: 'keenicon', name: 'notification' },
  //   routerLink: ['/push', 'coming-soon'],
  // },
  // {
  //   id: 'notifications_chat',
  //   name: 'SIDEBAR.NOTIFICATIONS',
  //   permission_page: 'communication_chat',
  //   icon: { type: 'keenicon', name: 'message-text' },
  //   routerLink: ['/chat', 'coming-soon'],
  // },
  // {
  //   id: 'training',
  //   name: 'SIDEBAR.TRAINING',
  //   permission_page: 'training',
  //   icon: { type: 'keenicon', name: 'clipboard' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.RESOURCES',
  //       permission_page: 'training',
  //       routerLink: ['/formacion', 'resources', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.INTERNAL_LAUNCH',
  //       permission_page: 'training',
  //       routerLink: ['/formacion', 'lanzamiento', 'coming-soon'],
  //     },
  //   ],
  // },
  // {
  //   id: 'resources',
  //   name: 'SIDEBAR.RESOURCES',
  //   permission_page: 'resources',
  //   icon: { type: 'keenicon', name: 'document' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.POSTERS',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'cartel', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.VIDEOS',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'video', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.GUIDES',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'guia', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.PROMO_LAUNCH',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'launch', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.PROMO_CAR',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'car', 'coming-soon'],
  //     },
  //     {
  //       name: 'SIDEBAR.PROMO_CHALLENGES',
  //       permission_page: 'resources',
  //       routerLink: ['/resources', 'desafio', 'coming-soon'],
  //     },
  //   ],
  // },
  // {
  //   id: 'administration_section_title',
  //   name: 'SIDEBAR.ADMINISTRATION',
  //   section_title: true,
  // },
  // {
  //   id: 'admin_corporations',
  //   name: 'SIDEBAR.CORPORATIONS',
  //   permission_page: 'admin_corporations',
  //   icon: { type: 'fas', name: 'fa-building' },
  //   routerLink: ['admin', 'companies'],
  // },
  // {
  //   id: 'admin_offices',
  //   name: 'SIDEBAR.OFFICES',
  //   permission_page: 'admin_offices',
  //   icon: { type: 'keenicon', name: 'map' },
  //   routerLink: ['admin', 'company', 'offices'],
  // },
  // {
  //   id: 'admin_shifts',
  //   name: 'SIDEBAR.SHIFTS',
  //   permission_page: 'admin_shifts',
  //   icon: { type: 'keenicon', name: 'calendar' },
  //   routerLink: ['admin', 'company', 'shifts'],
  // },

  {
    id: 'footprint_section_title',
    name: 'SIDEBAR.FOOTPRINT_SECTION',
    section_title: true,
  },
  {
    id: 'admin_footprint',
    name: 'SIDEBAR.FOOTPRINT_SECTION',
    permission_page: 'admin_footprint',
    icon: { type: 'svg', name: 'fingerprint' },
    submenus: [
      {
        name: 'SIDEBAR.USERS',
        permission_page: 'admin_footprint',
        routerLink: ['/admin', 'footPrint'],
      },
      {
        name: 'SIDEBAR.FOOTPRINT_RAW',
        permission_page: 'admin_footprint',
        routerLink: ['/admin', 'footPrint', 'raw-data'],
      },
    ],
  },
  {
    id: 'admin_travel_review',
    name: 'SIDEBAR.TRAVEL_REVIEW',
    permission_page: 'admin_travel_review',
    icon: { type: 'svg', name: 'clipboard-check' },
    routerLink: ['/admin', 'travelReview'],
  },
  {
    id: 'admin_corridors_review',
    name: 'SIDEBAR.CORRIDORS_REVIEW',
    permission_page: 'admin_corridors_review',
    icon: { type: 'svg', name: 'signpost' },
    routerLink: ['/admin', 'corridors-review'],
  },

  {
    id: 'admin_tags',
    name: 'SIDEBAR.TAGS',
    permission_page: 'admin_tags',
    icon: { type: 'svg', name: 'tags' },
    routerLink: ['/admin', 'tags'],
  },
  {
    id: 'app_section_title',
    name: 'SIDEBAR.APP_SECTION',
    section_title: true,
  },
  {
    id: 'admin_valoration_labels',
    name: 'SIDEBAR.VALORATION_LABELS',
    permission_page: 'admin_valoration_labels',
    icon: { type: 'svg', name: 'star' },
    routerLink: ['/admin', 'valoration-labels'],
  },
  // Catálogo de etiquetas de usuarios y rutas de pooling. Distinto de `admin_tags`,
  // que son las etiquetas de huella (puntos y segmentos).
  {
    id: 'admin_labels',
    name: 'SIDEBAR.LABELS',
    permission_page: 'admin_labels',
    icon: { type: 'svg', name: 'tag' },
    routerLink: ['/admin', 'labels'],
  },
  {
    id: 'match_section_title',
    name: 'SIDEBAR.MATCH_SECTION',
    section_title: true,
  },
  {
    id: 'admin_match_review',
    name: 'SIDEBAR.MATCH_REVIEW',
    permission_page: 'admin_match_review',
    icon: { type: 'svg', name: 'link' },
    routerLink: ['/admin', 'match-review'],
  },
  {
    id: 'admin_matches_listing',
    name: 'SIDEBAR.MATCHES_LISTING',
    permission_page: 'admin_match_list',
    icon: { type: 'svg', name: 'list' },
    routerLink: ['/admin', 'match-list'],
    // La ficha de un match (/admin/match/:id) también marca este item.
    // No coincide con /admin/match-review: se compara con «/admin/match/».
    activeRoutes: ['/admin/match'],
  },
  {
    id: 'admin_match_list',
    name: 'SIDEBAR.MATCH_LIST',
    permission_page: 'admin_match_list',
    icon: { type: 'svg', name: 'sliders-horizontal' },
    routerLink: ['/admin', 'match-config-variables'],
  },
  {
    id: 'admin_pois',
    name: 'SIDEBAR.POIS',
    permission_page: 'admin_match_list',
    icon: { type: 'svg', name: 'map-pin' },
    routerLink: ['/admin', 'pois'],
  },

  {
    id: 'configuration_section_title',
    name: 'SIDEBAR.CONFIGURATIONS',
    section_title: true,
  },
  {
    id: 'config_credits',
    name: 'SIDEBAR.CREDIT_TYPES',
    permission_page: 'config_credits',
    icon: { type: 'svg', name: 'credit-card' },
    routerLink: ['config', 'credits'],
  },
  {
    id: 'config_commissions',
    name: 'SIDEBAR.COMMISSIONS',
    permission_page: 'config_commissions',
    icon: { type: 'svg', name: 'circle-percent' },
    routerLink: ['config', 'commisions'],
  },
  {
    id: 'config_fraud_rules',
    name: 'SIDEBAR.FRAUD_RULES',
    // Mismo permiso que la página de Fraude, que es donde estaba antes.
    permission_page: 'admin_users',
    icon: { type: 'svg', name: 'shield-rules' },
    routerLink: ['config', 'fraud-rules'],
  },
  {
    id: 'config_variables',
    name: 'SIDEBAR.CONFIG_VARIABLES',
    permission_page: 'config_variables',
    icon: { type: 'svg', name: 'sliders-vertical' },
    routerLink: ['config', 'variables'],
  },
  // Comunicado que enseña la app al abrirse (WS-2391). Edita dos variables de
  // configuración, así que lleva el mismo permiso que Variables.
  {
    id: 'config_app_announcement',
    name: 'SIDEBAR.APP_ANNOUNCEMENT',
    permission_page: 'config_variables',
    icon: { type: 'svg', name: 'megaphone' },
    routerLink: ['config', 'app-announcement'],
  },
  // Respuestas rápidas y recordatorios del chat de soporte (WS-2271). Mismo
  // permiso que Variables: quien puede tocar la configuración puede editarlos;
  // el back exige lo mismo en las rutas de escritura.
  {
    id: 'config_support_snippets',
    name: 'WAIIS.SUPPORT_TOOLS.SNIPPETS_CONFIG.MENU',
    permission_page: 'config_variables',
    icon: { type: 'svg', name: 'message-square-text' },
    routerLink: ['config', 'support-snippets'],
  },
  {
    id: 'config_terms_conditions',
    name: 'SIDEBAR.TERMS_CONDITIONS',
    permission_page: 'config_terms_conditions',
    icon: { type: 'svg', name: 'file-text' },
    routerLink: ['config', 'terms-conditions'],
  },
  {
    id: 'config_contract_templates',
    name: 'SIDEBAR.CONTRACT_TEMPLATES',
    permission_page: 'config_contract_templates',
    icon: { type: 'svg', name: 'file-pen' },
    routerLink: ['config', 'contract-templates'],
  },
  {
    id: 'config_data_processing_agreement',
    name: 'SIDEBAR.DATA_PROCESSING_AGREEMENT',
    permission_page: 'config_data_processing_agreement',
    icon: { type: 'svg', name: 'shield-check' },
    routerLink: ['config', 'data-processing-agreement'],
  },
  {
    id: 'config_pmst_goal',
    name: 'SIDEBAR.PMST_GOAL_CONFIG',
    permission_page: 'config_pmst_goal',
    icon: { type: 'svg', name: 'target' },
    routerLink: ['config', 'pmst-goal'],
  },
  // Editor del cuestionario de empresa PMST (pasos/preguntas). Sin permiso
  // asignado todavía en el backend: de momento solo lo ve el superadmin
  // (que ve el menú entero sin filtrar), igual que rides_config.
  {
    id: 'config_pmst_questionnaire',
    name: 'SIDEBAR.PMST_QUESTIONNAIRE_CONFIG',
    permission_page: 'config_pmst_questionnaire',
    icon: { type: 'svg', name: 'clipboard-list' },
    routerLink: ['config', 'pmst-questionnaire'],
  },
  // Consulta de respuestas ya enviadas (GET .../forms/{formId}/responses,
  // solo lectura) — mismo patrón sin permiso asignado todavía.
  {
    id: 'config_pmst_questionnaire_responses',
    name: 'SIDEBAR.PMST_QUESTIONNAIRE_RESPONSES',
    permission_page: 'config_pmst_questionnaire_responses',
    icon: { type: 'svg', name: 'list-checks' },
    routerLink: ['config', 'pmst-questionnaire-responses'],
  },
  // Configuración de rides. Sigue el patrón de marketing_referrals: el permiso
  // 'rides_config' no está asignado a ningún rol todavía, así que la sección
  // solo la ve el superadmin (que ve el menú entero sin filtrar) y el backend
  // exige lo mismo en sus policies.
  {
    id: 'rides_config',
    name: 'SIDEBAR.RIDES_CONFIG',
    permission_page: 'rides_config',
    icon: { type: 'svg', name: 'car' },
    submenus: [
      {
        name: 'SIDEBAR.RIDES_CONFIG_SETTINGS',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'settings'],
      },
      {
        name: 'SIDEBAR.RIDES_CONFIG_GUEST_PHONES',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'guest-phones'],
      },
      {
        name: 'SIDEBAR.RIDES_CONFIG_PROVIDERS',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'providers'],
      },
      {
        name: 'SIDEBAR.RIDES_CONFIG_PROVIDER_ROUTING',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'provider-routing'],
      },
      {
        name: 'SIDEBAR.RIDES_CONFIG_BOOKINGS',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'booking-settings'],
      },
      {
        name: 'SIDEBAR.RIDES_CONFIG_REVIEW',
        permission_page: 'rides_config',
        routerLink: ['config', 'rides', 'review-settings'],
      },
    ],
  },
  // Configuración de la verificación facial. Mismo patrón que rides_config: el
  // permiso 'face_verification_config' no está asignado a ningún rol todavía,
  // así que en el menú la sección solo la ve el superadmin (que ve el menú
  // entero sin filtrar). El backend admite al superadmin y a quien tenga los
  // operation_id face-verification.settings.show/.update, y lo comprueba en
  // el middleware de backoffice.
  {
    id: 'face_verification_config',
    name: 'SIDEBAR.FACE_VERIFICATION_CONFIG',
    permission_page: 'face_verification_config',
    icon: { type: 'svg', name: 'scan-face' },
    routerLink: ['config', 'face-verification'],
  },
  // { id: 'developer_section_title', name: 'Crafted', section_title: true },
  // {
  //   id: 'dev_pages',
  //   name: 'SIDEBAR.PAGES',
  //   permission_page: 'dev_pages',
  //   icon: { type: 'keenicon', name: 'element-plus' },
  //   submenus: [
  //     {
  //       id: 'dev_profile',
  //       name: 'SIDEBAR.PROFILE',
  //       permission_page: 'dev_pages',
  //       submenus: [
  //         {
  //           name: 'SIDEBAR.OVERVIEW',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'profile', 'overview'],
  //         },
  //         {
  //           name: 'SIDEBAR.PROJECTS',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'profile', 'projects'],
  //         },
  //         {
  //           name: 'SIDEBAR.CAMPAIGNS',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'profile', 'campaigns'],
  //         },
  //         {
  //           name: 'SIDEBAR.DOCUMENTS',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'profile', 'documents'],
  //         },
  //         {
  //           name: 'SIDEBAR.CONNECTIONS',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'profile', 'connections'],
  //         },
  //       ],
  //     },
  //     {
  //       id: 'dev_wizards',
  //       name: 'SIDEBAR.WIZARDS',
  //       permission_page: 'dev_pages',
  //       submenus: [
  //         {
  //           name: 'SIDEBAR.HORIZONTAL',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'wizards', 'horizontal'],
  //         },
  //         {
  //           name: 'SIDEBAR.VERTICAL',
  //           permission_page: 'dev_pages',
  //           routerLink: ['/crafted', 'pages', 'wizards', 'vertical'],
  //         },
  //       ],
  //     },
  //   ],
  // },
  // {
  //   id: 'dev_accounts',
  //   name: 'SIDEBAR.ACCOUNTS',
  //   permission_page: 'dev_accounts',
  //   icon: { type: 'keenicon', name: 'profile-circle' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.OVERVIEW',
  //       permission_page: 'dev_accounts',
  //       routerLink: ['/crafted', 'account', 'overview'],
  //     },
  //     {
  //       name: 'SIDEBAR.SETTINGS',
  //       permission_page: 'dev_accounts',
  //       routerLink: ['/crafted', 'account', 'settings'],
  //     },
  //   ],
  // },
  // {
  //   id: 'dev_errors',
  //   name: 'SIDEBAR.ERRORS',
  //   permission_page: 'dev_errors',
  //   icon: { type: 'keenicon', name: 'phone' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.ERROR_404',
  //       permission_page: 'dev_errors',
  //       routerLink: ['/error', '404'],
  //     },
  //     {
  //       name: 'SIDEBAR.ERROR_500',
  //       permission_page: 'dev_errors',
  //       routerLink: ['/error', '500'],
  //     },
  //   ],
  // },
  // {
  //   id: 'dev_widgets',
  //   name: 'SIDEBAR.WIDGETS',
  //   permission_page: 'dev_widgets',
  //   icon: { type: 'keenicon', name: 'element-7' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.LISTS',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'lists'],
  //     },
  //     {
  //       name: 'SIDEBAR.STATISTICS',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'statistics'],
  //     },
  //     {
  //       name: 'SIDEBAR.CHARTS',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'charts'],
  //     },
  //     {
  //       name: 'SIDEBAR.MIXED',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'mixed'],
  //     },
  //     {
  //       name: 'SIDEBAR.TABLES',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'tables'],
  //     },
  //     {
  //       name: 'SIDEBAR.FEEDS',
  //       permission_page: 'dev_widgets',
  //       routerLink: ['/crafted', 'widgets', 'feeds'],
  //     },
  //   ],
  // },
  // { id: 'dev_apps_section_title', name: 'Apps', section_title: true },
  // {
  //   id: 'dev_chat',
  //   name: 'SIDEBAR.CHAT',
  //   permission_page: 'dev_chat',
  //   icon: { type: 'keenicon', name: 'message-text-2' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.PRIVATE_CHAT',
  //       permission_page: 'dev_chat',
  //       routerLink: ['/apps', 'chat', 'private-chat'],
  //     },
  //     {
  //       name: 'SIDEBAR.GROUP_CHAT',
  //       permission_page: 'dev_chat',
  //       routerLink: ['/apps', 'chat', 'group-chat'],
  //     },
  //     {
  //       name: 'SIDEBAR.DRAWER_CHAT',
  //       permission_page: 'dev_chat',
  //       routerLink: ['/apps', 'chat', 'drawer-chat'],
  //     },
  //   ],
  // },
  // {
  //   id: 'dev_user_management',
  //   name: 'SIDEBAR.USER_MANAGEMENT',
  //   permission_page: 'dev_user_management',
  //   icon: { type: 'keenicon', name: 'profile-user' },
  //   submenus: [
  //     {
  //       name: 'SIDEBAR.USERS_GENERIC',
  //       permission_page: 'dev_user_management',
  //       routerLink: ['/apps', 'users'],
  //     },
  //     {
  //       name: 'SIDEBAR.ROLES',
  //       permission_page: 'dev_user_management',
  //       routerLink: ['/apps', 'roles'],
  //     },
  //     {
  //       name: 'SIDEBAR.PERMISSIONS',
  //       permission_page: 'dev_user_management',
  //       routerLink: ['/apps', 'permissions'],
  //     },
  //   ],
  // },
];
